function tryStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

const EMPTY_ERROR_MESSAGE =
  "The server returned an empty error with no details - check the server logs"

export function extractMessageFromError(value: unknown): string {
  const message = extractRawMessage(value)
  // Something upstream may already have stringified an empty error object.
  return message.trim() === "{}" ? EMPTY_ERROR_MESSAGE : message
}

function extractRawMessage(value: unknown): string {
  if (value instanceof Error) {
    return value.message || value.name
  } else if (typeof value === "string" && value) {
    return value
  } else if (typeof value === "object" && value !== null) {
    // Plain objects thrown by clients (e.g. skeem/fileboy responses) often carry the reason
    // under one of these keys. A bare JSON.stringify would otherwise lose it (e.g. "{}").
    for (const key of ["error", "message", "errors", "statusText", "reason"] as const) {
      if (key in value) {
        const nested = extractRawMessage((value as Record<string, unknown>)[key])
        if (nested && nested !== "{}" && nested !== "[]" && nested !== EMPTY_ERROR_MESSAGE) {
          return nested
        }
      }
    }
    const json = tryStringify(value)
    if (json !== "{}") {
      return json
    }
    // Non-enumerable props or a custom class: fall back to what we can see.
    const ctor = (value as object).constructor?.name
    if (ctor === "Object" && Object.getOwnPropertyNames(value).length === 0) {
      // The skeem client rethrows whatever the skeem server put in its response `error`. An
      // Error that the server failed to turn into an ErrorBox serialises to exactly this.
      return EMPTY_ERROR_MESSAGE
    }
    const str = String(value)
    return str !== "[object Object]" ? str : `Unknown error${ctor && ctor !== "Object" ? ` (${ctor})` : ""} - see the file-queue-error alert/server logs`
  }
  // undefined has no JSON form; callers rely on this being falsy ("no reason given").
  return value === undefined ? "" : tryStringify(value)
}
